/**
 * EventFilters — the filter bar the Figma draws above the Event Calendar: a search box
 * plus six single-selects, every one defaulting to "All" — and a seventh, Nomination, which
 * the design does not draw: it is what the BD home's "Open for Nomination" count links at.
 *
 * Its own component rather than part of EventCalendar because the state has to be
 * reachable from outside: the landing page owns it (from the URL) so the task counts can
 * link at a filtered view, and both the month grid and the table read the same answer.
 * `ListEvents` takes no filter parameters, so all narrowing happens here, in memory.
 *
 * The state is plain strings rather than AWSUI `Option` objects so it survives a round
 * trip through the query string ({@link filtersFromParams} / {@link paramsFromFilters}).
 * That is what makes "Events to Publish → the drafts, in table view" a real link rather
 * than an instruction to re-filter by hand on arrival.
 *
 * {@link filterEvents} is exported separately and is pure, so a host can count matches
 * without rendering anything.
 */

import FormField from '@amzn/awsui-components-react/polaris/form-field';
import Input from '@amzn/awsui-components-react/polaris/input';
import Select, { type SelectProps } from '@amzn/awsui-components-react/polaris/select';
import { useMemo } from 'react';
import type { Event } from '../../client/EventCalendarClient';
import {
  STATUS_LABELS,
  SUB_TYPE_LABELS,
  TYPE_ORDER,
  typeMeta,
} from '../../utils/eventTaxonomy';
import { isNominatable } from '../../utils/nominatableEvents';
import { BUSINESS_UNITS } from '../../businessUnits';
import './EventFilters.css';
import { EventTypeIcon } from '../EventTypeIcon';

/**
 * "Everything" for one select. A sentinel rather than an empty string, so an event
 * whose field is genuinely empty cannot accidentally match a specific value.
 */
export const ALL = '__ALL__';

const ALL_OPTION: SelectProps.Option = { value: ALL, label: 'All' };

/** One filter bar's worth of state. Every field is either {@link ALL} or a real value. */
export interface EventFilterState {
  /** Free text, matched against the event name. */
  search: string;
  type: string;
  subType: string;
  /** One of the event's `businessUnits` (the design's "Segment" is the BU). */
  bu: string;
  city: string;
  /** `contentStatus` — see the note on {@link STATUS_LABELS}. */
  status: string;
  owner: string;
  /**
   * {@link NOMINATION_OPEN} for events a BD can nominate to right now (the New Events cards'
   * predicate, isNominatable — for a nominator, only events for their BUs), or {@link ALL}.
   */
  nomination: string;
}

/** The Nomination filter's one narrowing value. */
export const NOMINATION_OPEN = 'OPEN';

export const EMPTY_FILTERS: EventFilterState = {
  search: '',
  type: ALL,
  subType: ALL,
  bu: ALL,
  city: ALL,
  status: ALL,
  owner: ALL,
  nomination: ALL,
};

/** True when nothing is narrowed — used to pick an empty state's wording. */
export function isEmptyFilter(f: EventFilterState): boolean {
  return (
    f.search.trim() === '' &&
    f.type === ALL &&
    f.subType === ALL &&
    f.bu === ALL &&
    f.city === ALL &&
    f.status === ALL &&
    f.owner === ALL &&
    f.nomination === ALL
  );
}

/**
 * Applies the bar to a list of events. Pure, so both hosts and the tests can call it
 * without rendering anything. `now` decides which nomination windows are open;
 * `bdBusinessUnits`, given for a caller who nominates, narrows "open" to their BUs' events so
 * the filter lists what the "Open for Nomination" tile counted.
 */
export function filterEvents(
  events: readonly Event[],
  f: EventFilterState,
  now: Date = new Date(),
  bdBusinessUnits?: readonly string[],
): Event[] {
  const needle = f.search.trim().toLowerCase();
  return events.filter((e) => {
    if (f.type !== ALL && e.eventType !== f.type) return false;
    if (f.subType !== ALL && e.eventSubType !== f.subType) return false;
    if (f.bu !== ALL && !(e.businessUnits ?? []).includes(f.bu)) return false;
    if (f.city !== ALL && e.city !== f.city) return false;
    if (f.status !== ALL && e.contentStatus !== f.status) return false;
    if (f.owner !== ALL && e.eventOwner !== f.owner) return false;
    if (f.nomination === NOMINATION_OPEN && !isNominatable(e, now, bdBusinessUnits)) return false;
    if (needle && !(e.eventName ?? '').toLowerCase().includes(needle)) return false;
    return true;
  });
}

// ── Query string ─────────────────────────────────────────────────────────────

/**
 * Query-string key per filter. `search` is `q` because that is what every other search
 * box on the web calls it; the rest match the field they filter.
 */
const PARAM: Record<keyof EventFilterState, string> = {
  search: 'q',
  type: 'type',
  subType: 'subType',
  bu: 'bu',
  city: 'city',
  status: 'status',
  owner: 'owner',
  nomination: 'nomination',
};

const SELECT_KEYS = [
  'type',
  'subType',
  'bu',
  'city',
  'status',
  'owner',
  'nomination',
] as const;

/** URL → filter state. A missing parameter means "All", never an empty match. */
export function filtersFromParams(params: URLSearchParams): EventFilterState {
  const next: EventFilterState = { ...EMPTY_FILTERS, search: params.get(PARAM.search) ?? '' };
  for (const key of SELECT_KEYS) next[key] = params.get(PARAM[key]) ?? ALL;
  return next;
}

/**
 * Filter state → URL parameters, written into `into` so a caller can keep its own
 * unrelated parameters (the view toggle) alongside them.
 *
 * Defaults are omitted rather than written as `type=__ALL__`, so a cleared bar leaves a
 * clean shareable URL and the sentinel never escapes the app.
 */
export function paramsFromFilters(
  f: EventFilterState,
  into: URLSearchParams = new URLSearchParams(),
): URLSearchParams {
  if (f.search.trim()) into.set(PARAM.search, f.search);
  else into.delete(PARAM.search);
  for (const key of SELECT_KEYS) {
    if (f[key] !== ALL) into.set(PARAM[key], f[key]);
    else into.delete(PARAM[key]);
  }
  return into;
}

// ── Options ──────────────────────────────────────────────────────────────────

/**
 * Options for a free-text field, gathered from the events themselves. The wire types
 * for city / owner are open strings with no enum to enumerate, so the data is
 * the only source; an empty list means nothing has that field set yet.
 */
function optionsFromData(
  events: readonly Event[],
  pick: (e: Event) => string | undefined,
): SelectProps.Option[] {
  const values = new Set<string>();
  for (const e of events) {
    const v = pick(e);
    if (v) values.add(v);
  }
  return [ALL_OPTION, ...[...values].sort().map((v) => ({ value: v, label: v }))];
}

const TYPE_OPTIONS: SelectProps.Option[] = [
  ALL_OPTION,
  ...TYPE_ORDER.map((t) => ({
    value: t,
    label: typeMeta(t).label,
    iconSvg: <EventTypeIcon type={t} />,
  })),
];

const SUB_TYPE_OPTIONS: SelectProps.Option[] = [
  ALL_OPTION,
  ...Object.entries(SUB_TYPE_LABELS).map(([value, label]) => ({ value, label })),
];

const STATUS_OPTIONS: SelectProps.Option[] = [
  ALL_OPTION,
  ...Object.entries(STATUS_LABELS).map(([value, label]) => ({ value, label })),
];

const BU_OPTIONS: SelectProps.Option[] = [
  ALL_OPTION,
  ...BUSINESS_UNITS.map((bu) => ({ value: bu, label: bu })),
];

/** "All" for a caller limited to their own BUs: it means any of theirs, never every event. */
const ALL_MY_BUS_OPTION: SelectProps.Option = { value: ALL, label: 'All my BUs' };

/**
 * The BU options for a caller limited to `mine`: "All my BUs", then the fixed list's entries
 * that are one of theirs, compared trimmed and case-insensitively as utils/nominatableEvents
 * does. A BU of theirs that is not on the fixed list is dropped rather than offered — no event
 * can carry it, so it could only ever match nothing.
 */
function buOptionsFor(mine: readonly string[]): SelectProps.Option[] {
  const norm = (bu: string) => bu.trim().toLowerCase();
  const wanted = new Set(mine.map(norm));
  return [
    ALL_MY_BUS_OPTION,
    ...BU_OPTIONS.filter((o) => o.value !== ALL && wanted.has(norm(String(o.value)))),
  ];
}

const NOMINATION_OPTIONS: SelectProps.Option[] = [
  ALL_OPTION,
  { value: NOMINATION_OPEN, label: 'Open for Nomination' },
];

/**
 * Turns a stored value back into the option to show. A value that no longer exists in
 * the data — a city filter deep-linked from a stale URL, say — falls back to a
 * self-describing option rather than rendering as blank.
 */
function selected(value: string, options: SelectProps.Option[]): SelectProps.Option {
  if (value === ALL) return ALL_OPTION;
  return options.find((o) => o.value === value) ?? { value, label: value };
}

// ── Component ────────────────────────────────────────────────────────────────

export interface EventFiltersProps {
  /** Every event, so the open-string filters can offer the values actually present. */
  events: readonly Event[];
  value: EventFilterState;
  onChange: (next: EventFilterState) => void;
  /** Renders the search box. The calendar puts its own in the header row instead. */
  showSearch?: boolean;
  /**
   * Drops the Status select. A BD sees published events only, so for them it could only
   * ever offer one useful value. The host must also ignore `status` when filtering, or a
   * deep-linked `status=DRAFT` would narrow by a control the user cannot see.
   */
  hideStatus?: boolean;
  /**
   * Limits the BU select to these BUs (a BD's own, from useNominationBusinessUnits). Its
   * {@link ALL} reads "All my BUs", not "All": the host has already narrowed the events to
   * these BUs, so it means any of them — never every event. It is also the way back after
   * picking one. Omit to offer the whole fixed list under a plain "All".
   */
  businessUnits?: readonly string[];
}

export default function EventFilters({
  events,
  value,
  onChange,
  showSearch,
  hideStatus,
  businessUnits,
}: EventFiltersProps) {
  const buOptions = useMemo(
    () => (businessUnits ? buOptionsFor(businessUnits) : BU_OPTIONS),
    [businessUnits],
  );

  const cityOptions = useMemo(() => optionsFromData(events, (e) => e.city), [events]);
  const ownerOptions = useMemo(
    () => optionsFromData(events, (e) => e.eventOwner),
    [events],
  );

  const set = (patch: Partial<EventFilterState>) => onChange({ ...value, ...patch });

  /* The labels are visible (FormField) rather than aria-only: the Figma shows each one
     above its control, and an unlabelled "All" dropdown says nothing on its own. */
  return (
    <div className="eh-filters">
      {showSearch ? (
        <FormField label="Search">
          <Input
            type="search"
            value={value.search}
            onChange={({ detail }) => set({ search: detail.value })}
            placeholder="Enter value"
            ariaLabel="Search events by name"
          />
        </FormField>
      ) : null}
      <FormField label="Event Type">
        <Select
          selectedOption={selected(value.type, TYPE_OPTIONS)}
          onChange={({ detail }) => set({ type: String(detail.selectedOption.value) })}
          options={TYPE_OPTIONS}
        />
      </FormField>
      <FormField label="Event Sub Type">
        <Select
          selectedOption={selected(value.subType, SUB_TYPE_OPTIONS)}
          onChange={({ detail }) => set({ subType: String(detail.selectedOption.value) })}
          options={SUB_TYPE_OPTIONS}
        />
      </FormField>
      {/* "Segment" in the Figma, but what it means is the BU — filtered on the event's
          businessUnits (an event matches when any of its BUs is the one chosen), from the one
          fixed BU list. The event's free-form `segment` field is not set anywhere. */}
      <FormField label="BU">
        <Select
          selectedOption={
            businessUnits && value.bu === ALL ? ALL_MY_BUS_OPTION : selected(value.bu, buOptions)
          }
          onChange={({ detail }) => set({ bu: String(detail.selectedOption.value) })}
          options={buOptions}
          filteringType="auto"
        />
      </FormField>
      <FormField label="City">
        <Select
          selectedOption={selected(value.city, cityOptions)}
          onChange={({ detail }) => set({ city: String(detail.selectedOption.value) })}
          options={cityOptions}
        />
      </FormField>
      {hideStatus ? null : (
        <FormField label="Status">
          <Select
            selectedOption={selected(value.status, STATUS_OPTIONS)}
            onChange={({ detail }) => set({ status: String(detail.selectedOption.value) })}
            options={STATUS_OPTIONS}
          />
        </FormField>
      )}
      <FormField label="Marketing Event Owner">
        <Select
          selectedOption={selected(value.owner, ownerOptions)}
          onChange={({ detail }) => set({ owner: String(detail.selectedOption.value) })}
          options={ownerOptions}
        />
      </FormField>
      <FormField label="Nomination">
        <Select
          selectedOption={selected(value.nomination, NOMINATION_OPTIONS)}
          onChange={({ detail }) => set({ nomination: String(detail.selectedOption.value) })}
          options={NOMINATION_OPTIONS}
        />
      </FormField>
    </div>
  );
}
